import React, { useEffect, useState } from "react";
import { Card, Col, Collapse, Row, Spin, Tag, Typography } from "antd";
import { Company, Supplier } from "../../types/booking";
import { fetchAgentCompanyContactEmails } from "../../services/agentPortalService";

const { Text } = Typography;

interface AgentBookingContactsCardProps {
  company: Company | null | undefined;
  supplier: Supplier | null | undefined;
  agentId: number;
}

const compactLabel: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  lineHeight: 1.2,
  marginBottom: 2,
};

const compactValue: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  lineHeight: 1.35,
  marginBottom: 8,
};

const formatAddress = (company: Company): string => {
  const parts = [
    [company.street, company.house_number].filter(Boolean).join(" "),
    [company.postal_code, company.city].filter(Boolean).join(" "),
    company.country,
  ].filter(Boolean);
  return parts.join(", ") || company.address || "-";
};

const DetailLine: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <Text type="secondary" style={compactLabel}>
      {label}
    </Text>
    <span style={compactValue}>{value}</span>
  </div>
);

export const AgentBookingContactsCard: React.FC<AgentBookingContactsCardProps> = ({
  company,
  supplier,
  agentId,
}) => {
  const [emails, setEmails] = useState<{ email: string; source: "user" | "invoice" }[]>(
    []
  );
  const [loadingEmails, setLoadingEmails] = useState(false);

  useEffect(() => {
    if (!company?.id) {
      setEmails([]);
      return;
    }

    setLoadingEmails(true);
    fetchAgentCompanyContactEmails(agentId, company.id).then(({ data }) => {
      setEmails(data);
      setLoadingEmails(false);
    });
  }, [agentId, company?.id]);

  const supplierProvince = (supplier as Supplier & { province?: string })?.province;

  return (
    <Card
      size="small"
      title="Company & supplier"
      styles={{ body: { padding: "10px 12px" } }}
    >
      <Row gutter={[12, 8]}>
        <Col xs={24} sm={12}>
          {!company ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No company linked
            </Text>
          ) : (
            <>
              <DetailLine label="Company" value={company.name} />
              <DetailLine
                label="Emails"
                value={
                  loadingEmails ? (
                    <Spin size="small" />
                  ) : emails.length > 0 ? (
                    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {emails.map((entry) => (
                        <span key={entry.email}>
                          <Text copyable style={{ fontFamily: "monospace", fontSize: 11 }}>
                            {entry.email}
                          </Text>
                          {entry.source === "invoice" && (
                            <Tag style={{ marginLeft: 4, fontSize: 10, lineHeight: "16px" }}>
                              invoice
                            </Tag>
                          )}
                        </span>
                      ))}
                    </span>
                  ) : (
                    company.invoice_email || "-"
                  )
                }
              />
              <DetailLine label="Phone" value={company.phone_number || "-"} />
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: "company-more",
                    label: (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        More company details
                      </Text>
                    ),
                    children: (
                      <div style={{ paddingBottom: 4 }}>
                        <DetailLine label="Address" value={formatAddress(company)} />
                        <DetailLine label="VAT" value={company.vat_number || "-"} />
                        <DetailLine label="EORI" value={company.eori_number || "-"} />
                        <DetailLine
                          label="CoC"
                          value={company.coc_number != null ? company.coc_number : "-"}
                        />
                        {company.invoice_email && emails.length > 0 && (
                          <DetailLine
                            label="Invoice email"
                            value={company.invoice_email}
                          />
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Col>

        <Col xs={24} sm={12}>
          {!supplier ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No supplier linked
            </Text>
          ) : (
            <>
              <DetailLine label="Supplier" value={supplier.name} />
              <DetailLine label="Email" value={supplier.email || "-"} />
              <DetailLine label="Phone" value={supplier.phone || "-"} />
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: "supplier-more",
                    label: (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        More supplier details
                      </Text>
                    ),
                    children: (
                      <div style={{ paddingBottom: 4 }}>
                        <DetailLine label="Country" value={supplier.country || "-"} />
                        <DetailLine label="Province" value={supplierProvince || "-"} />
                        <DetailLine label="City" value={supplier.city || "-"} />
                        <DetailLine label="Address" value={supplier.address || "-"} />
                      </div>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Col>
      </Row>
    </Card>
  );
};
